import {
  CheckCircle,
  Hourglass,
  Share,
  Undo2,
  UserCircle,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { View, YStack } from "tamagui";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { DotWizard } from "../../ui/DotWizard";
import { PopoverConfirm } from "../../ui/PopoverConfirm";
import { TipsText } from "../../ui/TipsText";
import type BaseModuleModel from "../core/BaseModuleModel";

export const DoneFixedFooter: React.FC<{
  // biome-ignore lint/suspicious/noExplicitAny: Because
  module: BaseModuleModel<any, any>;
  userIsDone: boolean;
  waitingForUsers: string[];
  description: string | JSX.Element;
}> = observer(({ module, userIsDone, waitingForUsers, description }) => {
  const totalUsers = module?.plan?.usersCount ?? 0;

  const userIsDoneAndPlanHasUsers = userIsDone && module.plan.usersCount > 1;

  return (
    <View justifyContent="center" alignItems="center">
      <YStack
        borderTopColor={"$divider"}
        borderTopWidth={1}
        backgroundColor={"$backgroundBlock"}
        justifyContent="center"
        padding="$medium"
        paddingHorizontal={"$larger"}
        gap={"$medium"}
        maxWidth={600}
        width={"100%"}
      >
        {!!description && (
          <Body muted numberOfLines={2}>
            {description}
          </Body>
        )}
        <DotWizard
          dots={[
            {
              completed: userIsDone,
              icon: UserCircle,
              percent: userIsDone ? 1 : 0,
            },
            {
              completed: userIsDoneAndPlanHasUsers,
              icon: Hourglass,
              percent: userIsDoneAndPlanHasUsers
                ? 1 - waitingForUsers.length / totalUsers
                : 0,
            },
            { completed: false, icon: CheckCircle },
          ]}
        />
      </YStack>
    </View>
  );
});

export const DoneBox: React.FC<{
  // biome-ignore lint/suspicious/noExplicitAny: Because
  module: BaseModuleModel<any, any>;
  doneDescription: string;
  beforeDoneDescription: string | JSX.Element;
  userReadyUpDescription: string;
  everyoneReadyDescription: string;
  userIsDone: boolean;
  waitingForUsers: string[];
  ctaLabel: string;
}> = observer(
  ({
    module,
    userIsDone,
    waitingForUsers,
    doneDescription,
    beforeDoneDescription,
    everyoneReadyDescription,
    userReadyUpDescription,
    ctaLabel = "We are done",
  }) => {
    const planUsers = module?.plan.usersCount ?? 1;

    const onDone = module?.isDone ? undefined : module?.onDone.bind(module);

    const plan = module.plan;
    const _currentUser = plan.userInPlan;
    // const creatorUser = usersStore.byId.get(plan.creatorUserUid);
    // const isCreatorUser = currentUser?.uid === plan.creatorUserUid;

    let description: JSX.Element | string = "";

    if (planUsers < 2) {
      description =
        "Share and wait for everyone to contribute before making a decision together.";
    } else if (waitingForUsers.length === 0) {
      description = everyoneReadyDescription;
    } else if (!userIsDone) {
      description = userReadyUpDescription;
    } else {
      description = beforeDoneDescription;
    }

    if (onDone) {
      const totalUsers = module?.plan?.usersCount ?? 0;

      let helpText: string | undefined;

      const singleUserPlan = module.plan.usersCount <= 1;
      const userIsDoneAndPlanHasUsers =
        userIsDone && module.plan.usersCount > 1;

      const waitingForUsersString = waitingForUsers.join(", ");

      if (module.plan.usersCount < 2) {
        helpText =
          "Invite members and decide together as a group before marking this step as finished";
      } else if (waitingForUsers.length > 0) {
        //         helpText = `${isCreatorUser ? "You" : creatorUser?.data.displayName} can this step to finished right now - but we're still waiting for answers from ${waitingForUsersString}`;
        helpText = `You can decide to set this step to finished right now - but we're still waiting for answers from ${waitingForUsersString}`;
      }

      const isReadyForDone =
        !userIsDoneAndPlanHasUsers || waitingForUsers.length > 0;

      return (
        <View>
          <Box>
            <Body>{description}</Body>
            <DotWizard
              dots={[
                {
                  completed: userIsDone,
                  icon: UserCircle,
                  percent: userIsDone ? 1 : 0,
                },
                {
                  completed: userIsDoneAndPlanHasUsers,
                  icon: Hourglass,
                  percent: userIsDoneAndPlanHasUsers
                    ? 1 - waitingForUsers.length / totalUsers
                    : 0,
                },
                { completed: false, icon: CheckCircle },
              ]}
            />

            {helpText && <TipsText text={helpText} />}
          </Box>

          <YStack gap="$medium">
            {planUsers < 2 && !plan.businessPlan && (
              <Button
                type={"primary"}
                onPress={() => {
                  module?.plan.setShowShare(true);
                }}
                icon={Share}
              >
                Share / Invite
              </Button>
            )}

            <PopoverConfirm
              placement="top"
              skip={!isReadyForDone}
              confirmTitle={
                singleUserPlan
                  ? "Are you sure?"
                  : "Not everyone has finished deciding!"
              }
              type="warning"
              confirmDescription={
                singleUserPlan
                  ? `You can decide to set this step as finished right now, but you haven't invited anyone yet, and they won't be able to respond.`
                  : `Are you sure you want to set this step as finished? We're still waiting for answers from ${waitingForUsersString}.`
              }
              onConfirm={onDone}
            >
              <Button
                disabled={!userIsDone}
                type={isReadyForDone ? "secondary" : "primary"}
                icon={CheckCircle}
              >
                {ctaLabel}
              </Button>
            </PopoverConfirm>
          </YStack>
        </View>
      );
    }

    return (
      <>
        <Box>
          <Body>{doneDescription}</Body>
        </Box>
        <Button
          type="secondary"
          onPress={module.toggleDoneState.bind(module)}
          icon={Undo2}
        >
          Revert done status
        </Button>
      </>
    );
  },
);
